/**
 * The one definition of an address a LOCAL account can hold.
 *
 * It exists because there were two, and they disagreed in the direction that
 * silently breaks an invitation. Creation validated `^[^@\s]+@[^@\s]+$` up to
 * 320 characters — the storable floor the column CHECK enforces — while
 * redemption required a dot in the domain and capped at 255. An address like
 * `dana@localhost`, or any address between 256 and 320 characters, was
 * therefore INVITABLE and UN-REDEEMABLE: the row stored, the email sent, and
 * the invitee met the same single 404 the design uses for an expired or spent
 * link, which by construction cannot tell them the address was the problem. The
 * administrator had no signal either.
 *
 * Fixing it by loosening redemption was the wrong direction — the same rule
 * guards `provisionLocalAccount`, so widening it would let addresses into
 * Keycloak that this service currently refuses. Creation is the place to fail:
 * it fails in front of the person who typed the address and can correct it.
 *
 * It lives in `@playbookai/types` rather than in the auth service because the
 * invite dialog enforces it too. A second regex in the shell, kept in step by
 * hand, is the same failure this module was written to end — one definition,
 * two consumers, and the server still the authoritative gate.
 *
 * NOT RFC 5322, deliberately, and the invitation controller's own comment makes
 * the case: a regex strict enough to be "correct" rejects addresses real
 * directories hold. This is only the floor both ends already agreed they needed
 * — one `@`, something either side, a dotted domain, no whitespace.
 */
/** Redemption's ceiling, which is the binding one. The column allows 320. */
export const LOCAL_EMAIL_MAX = 255;
/**
 * The domain is split into LABELS, and that is the whole point of the shape.
 *
 * The rule used to be `[^\s@]+\.[^\s@]+`, which reads like "a dot in the
 * domain" and is not: `[^\s@]` also matches a dot, so `b..com` satisfied it as
 * `b.` + `.` + `com`. It proved a dot existed, never that the labels around it
 * did — so `a@b..com`, `a@..com`, `a@b...com`, `a@b..` and `a@b.com.` were all
 * invitable, and an empty label is a name the DNS cannot hold. Excluding the
 * dot from the label class and repeating the group is what makes each label
 * non-empty, and `+` on the group is what still requires at least two of them.
 *
 * The LOCAL part is deliberately untouched: nothing observed it, and tightening
 * it would refuse addresses this workspace accepts today for no reported cause.
 */
const LOCAL_EMAIL_SHAPE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
/**
 * Whether an address can be REDEEMED, tested the way redemption tests it:
 * trimmed and lower-cased first, because that is the form the check runs on and
 * a caller comparing the raw value would disagree at the edges.
 */
export function isRedeemableLocalEmail(value) {
    const email = value.trim().toLowerCase();
    return (email.length > 0 &&
        email.length <= LOCAL_EMAIL_MAX &&
        LOCAL_EMAIL_SHAPE.test(email));
}
//# sourceMappingURL=local-email.js.map